import { useState, useRef, useEffect, useCallback } from "react";

import { Button, View, Text } from "../components/base";
import { Icon } from "../components/base/Icon";
import { PageLayout } from "../components/PageLayout";
import { API_URL, TRANSCRIBE_WS_URL } from "../config";
import { useSTTProviders, STTModel, STTModelCapabilities } from "../hooks/useSTTProviders";

// --- Types ---

type TranscribeEvent = {
  id: number;
  timestamp: Date;
  type: string;
  text?: string;
  raw: Record<string, unknown>;
};

type ConnectionStatus = "disconnected" | "connecting" | "connected" | "listening";

type PanelRef = {
  ws: WebSocket | null;
  eventId: number;
};

type PanelConfig = {
  modelValue: string;
  language: string;
  prompt: string;
  endpointing: number | null;
  externalEndpointing: number | null;
  turnTakingMode: "provider" | "silero" | "smart_turn" | null;
  noiseReductionType: string | null;
  vadMode: "server_vad" | "semantic_vad" | null;
  vadEagerness: "low" | "medium" | "high" | null;
  eotThreshold: number | null;
  eotTimeoutMs: number | null;
  smartTurnVadStopSecs: number | null;
  smartTurnStopSecs: number | null;
  smartTurnTimeout: number | null;
};

type ModelPanel = {
  id: string;
  config: PanelConfig;
  events: TranscribeEvent[];
};

// --- Audio helpers ---

const SAMPLE_RATE = 16000;
const CHUNK_SAMPLES = 320; // 20ms at 16kHz

const LANGUAGE_LABELS: Record<string, string> = {
  auto: "Auto-Detect",
  multi: "Auto-Detect",
  af: "Afrikaans",
  am: "Amharic",
  ar: "Arabic",
  as: "Assamese",
  az: "Azerbaijani",
  ba: "Bashkir",
  be: "Belarusian",
  bg: "Bulgarian",
  bn: "Bengali",
  bo: "Tibetan",
  br: "Breton",
  bs: "Bosnian",
  ca: "Catalan",
  cs: "Czech",
  cy: "Welsh",
  da: "Danish",
  de: "German",
  el: "Greek",
  en: "English",
  es: "Spanish",
  et: "Estonian",
  eu: "Basque",
  fa: "Persian",
  fi: "Finnish",
  fil: "Filipino",
  fo: "Faroese",
  fr: "French",
  gl: "Galician",
  gu: "Gujarati",
  ha: "Hausa",
  haw: "Hawaiian",
  he: "Hebrew",
  hi: "Hindi",
  hr: "Croatian",
  ht: "Haitian Creole",
  hu: "Hungarian",
  hy: "Armenian",
  id: "Indonesian",
  is: "Icelandic",
  it: "Italian",
  ja: "Japanese",
  jw: "Javanese",
  ka: "Georgian",
  kk: "Kazakh",
  km: "Khmer",
  kn: "Kannada",
  ko: "Korean",
  la: "Latin",
  lb: "Luxembourgish",
  ln: "Lingala",
  lo: "Lao",
  lt: "Lithuanian",
  lv: "Latvian",
  mg: "Malagasy",
  mi: "Maori",
  mk: "Macedonian",
  ml: "Malayalam",
  mn: "Mongolian",
  mr: "Marathi",
  ms: "Malay",
  mt: "Maltese",
  my: "Burmese",
  ne: "Nepali",
  nl: "Dutch",
  nn: "Norwegian Nynorsk",
  no: "Norwegian",
  oc: "Occitan",
  pa: "Punjabi",
  pl: "Polish",
  ps: "Pashto",
  pt: "Portuguese",
  ro: "Romanian",
  ru: "Russian",
  sa: "Sanskrit",
  sd: "Sindhi",
  si: "Sinhala",
  sk: "Slovak",
  sl: "Slovenian",
  sn: "Shona",
  so: "Somali",
  sq: "Albanian",
  sr: "Serbian",
  su: "Sundanese",
  sv: "Swedish",
  sw: "Swahili",
  ta: "Tamil",
  te: "Telugu",
  tg: "Tajik",
  th: "Thai",
  tk: "Turkmen",
  tl: "Tagalog",
  tr: "Turkish",
  tt: "Tatar",
  uk: "Ukrainian",
  ur: "Urdu",
  uz: "Uzbek",
  vi: "Vietnamese",
  yi: "Yiddish",
  yo: "Yoruba",
  yue: "Cantonese",
  zh: "Chinese",
};

function float32ToInt16Base64(float32: Float32Array): string {
  const int16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(int16.buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function fetchWsToken(): Promise<string | null> {
  try {
    const res = await fetch(`${API_URL}/v1/auth/ws-token`, { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    return data.token || null;
  } catch {
    return null;
  }
}

function makePanelRef(): PanelRef {
  return { ws: null, eventId: 0 };
}

function makeDefaultConfig(modelValue: string): PanelConfig {
  return {
    modelValue,
    language: "en",
    prompt: "",
    endpointing: null,
    externalEndpointing: null,
    turnTakingMode: null,
    noiseReductionType: null,
    vadMode: null,
    vadEagerness: null,
    eotThreshold: null,
    eotTimeoutMs: null,
    smartTurnVadStopSecs: null,
    smartTurnStopSecs: null,
    smartTurnTimeout: null,
  };
}

function promptLabel(caps: STTModelCapabilities | undefined): string | null {
  if (!caps?.supportsPrompt) return null;
  if (caps.promptType === "keywords") return "Keywords";
  if (caps.promptType === "system") return "Context Prompt";
  return "Prompt";
}

function promptPlaceholder(caps: STTModelCapabilities | undefined): string {
  if (caps?.promptType === "keywords") return "";
  return "";
}

function resolveTurnTakingConfig(config: PanelConfig, caps: STTModelCapabilities | undefined) {
  const hasBuiltinVAD = !!caps?.hasBuiltinVAD;
  const supportsProviderEndpointing = caps?.supportsProviderEndpointing ?? (hasBuiltinVAD && !!caps?.supportsEndpointing);
  const supportsExternalEndpointing = caps?.supportsExternalEndpointing ?? (!hasBuiltinVAD && !!caps?.supportsEndpointing);
  const supportsTurnTakingMode = !!caps?.supportsTurnTakingMode || (hasBuiltinVAD && supportsProviderEndpointing && supportsExternalEndpointing);
  const supportsSmartTurn = !!caps?.supportsSmartTurn;
  const defaultTurnTakingMode: "provider" | "silero" | "smart_turn" = caps?.defaultTurnTakingMode || (hasBuiltinVAD ? "provider" : "silero");
  const selectedTurnTakingMode: "provider" | "silero" | "smart_turn" = config.turnTakingMode || defaultTurnTakingMode;
  const usingExternalTurnTaking = !hasBuiltinVAD || selectedTurnTakingMode === "silero";
  const showProviderEndpointing = !!supportsProviderEndpointing && !usingExternalTurnTaking && selectedTurnTakingMode !== "smart_turn";
  const showExternalEndpointing = !!supportsExternalEndpointing && usingExternalTurnTaking && selectedTurnTakingMode !== "smart_turn";

  return {
    hasBuiltinVAD,
    supportsProviderEndpointing,
    supportsExternalEndpointing,
    supportsTurnTakingMode,
    supportsSmartTurn,
    defaultTurnTakingMode,
    selectedTurnTakingMode,
    usingExternalTurnTaking,
    showProviderEndpointing,
    showExternalEndpointing,
  };
}

// --- Inline panel header with config ---

function PanelHeader({
  config,
  onChange,
  allModels,
  getModelByValue,
  getProviderForModel,
  disabled,
  canRemove,
  onRemove,
}: {
  config: PanelConfig;
  onChange: (patch: Partial<PanelConfig>) => void;
  allModels: STTModel[];
  getModelByValue: (v: string) => STTModel | undefined;
  getProviderForModel: (v: string) => { id: string } | undefined;
  disabled: boolean;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const model = getModelByValue(config.modelValue);
  const caps = model?.capabilities;
  const languages = caps?.languages ?? ["en"];
  const pLabel = promptLabel(caps);
  const turnTaking = resolveTurnTakingConfig(config, caps);

  const hasAdvanced = !!(
    caps?.supportsEndpointing ||
    turnTaking.supportsTurnTakingMode ||
    turnTaking.supportsExternalEndpointing ||
    caps?.supportsNoiseReduction ||
    caps?.supportsVADMode ||
    caps?.supportsEOT
  );

  const selectClass = "h-7 px-1.5 rounded border border-slate-700 bg-[#0f172a] text-[11px] text-slate-100 disabled:opacity-50 min-w-0";
  const inputClass =
    "h-7 px-1.5 rounded border border-slate-700 bg-[#0f172a] text-[11px] text-slate-100 disabled:opacity-50 placeholder:text-slate-500 min-w-0";
  const labelClass = "text-[10px] text-cyan-300/80";
  const headerClass = "flex flex-col border-b border-slate-700 bg-[#111827] rounded-t-lg";

  return (
    <View className={headerClass}>
      {/* Compact config row */}
      <View className="flex flex-row items-center gap-1.5 px-2 py-1.5">
        <select
          value={config.modelValue}
          onChange={(e) => {
            const newModel = getModelByValue(e.target.value);
            const newLangs = newModel?.capabilities.languages ?? ["en"];
            const langValid = newLangs.includes(config.language);
            const newTurnTaking = resolveTurnTakingConfig(makeDefaultConfig(e.target.value), newModel?.capabilities);
            onChange({
              ...makeDefaultConfig(e.target.value),
              turnTakingMode: newTurnTaking.defaultTurnTakingMode,
              ...(!langValid ? { language: newLangs[0] || "en" } : { language: config.language }),
            });
          }}
          disabled={disabled}
          className={`${selectClass} flex-1`}
        >
          {allModels.map((m) => {
            const p = getProviderForModel(m.value);
            return (
              <option
                key={m.value}
                value={m.value}
              >
                {m.label} ({p?.id ?? ""})
              </option>
            );
          })}
        </select>
        <select
          value={config.language}
          onChange={(e) => onChange({ language: e.target.value })}
          disabled={disabled}
          className={`${selectClass} w-20 shrink-0`}
        >
          {languages.map((lang) => (
            <option
              key={lang}
              value={lang}
            >
              {LANGUAGE_LABELS[lang] || lang}
            </option>
          ))}
        </select>
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className={`shrink-0 p-1 rounded transition-colors ${expanded ? "text-fuchsia-200 bg-fuchsia-900/30" : "text-fuchsia-400/70 hover:text-fuchsia-200"}`}
        >
          <Icon
            name="settings"
            size={14}
          />
        </button>
      </View>

      {/* Expanded settings */}
      {expanded && (
        <View className="flex flex-col gap-2 px-2 pb-2 pt-2 border-t border-slate-700">
          {/* Prompt / Keywords */}
          {pLabel && (
            <View className="flex flex-col gap-0.5">
              <Text className={labelClass}>{pLabel}</Text>
              <input
                type="text"
                value={config.prompt}
                onChange={(e) => onChange({ prompt: e.target.value })}
                disabled={disabled}
                placeholder={promptPlaceholder(caps)}
                className={inputClass}
              />
            </View>
          )}

          {/* Advanced: Turn-taking + Endpointing */}
          {turnTaking.supportsTurnTakingMode && (
            <View className="flex flex-col gap-0.5">
              <Text className={labelClass}>Turn Taking</Text>
              <select
                value={turnTaking.selectedTurnTakingMode}
                onChange={(e) => onChange({ turnTakingMode: e.target.value as "provider" | "silero" | "smart_turn" })}
                disabled={disabled}
                className={selectClass}
              >
                {turnTaking.hasBuiltinVAD && <option value="provider">Provider VAD</option>}
                <option value="silero">External (Silero)</option>
                {turnTaking.supportsSmartTurn && <option value="smart_turn">Smart Turn</option>}
              </select>
            </View>
          )}

          {turnTaking.showProviderEndpointing && (!caps?.supportsVADMode || config.vadMode === "server_vad") && (
            <View className="flex flex-col gap-0.5">
              <Text className={labelClass}>Provider Endpointing (ms)</Text>
              <input
                type="number"
                value={config.endpointing ?? caps?.defaultProviderEndpointing ?? caps?.defaultEndpointing ?? 300}
                onChange={(e) => onChange({ endpointing: e.target.value === "" ? null : Number(e.target.value) })}
                disabled={disabled}
                className={inputClass}
              />
            </View>
          )}

          {turnTaking.showExternalEndpointing && (
            <View className="flex flex-col gap-0.5">
              <Text className={labelClass}>External Endpointing (ms)</Text>
              <input
                type="number"
                value={config.externalEndpointing ?? config.endpointing ?? caps?.defaultExternalEndpointing ?? caps?.defaultEndpointing ?? 300}
                onChange={(e) => onChange({ externalEndpointing: e.target.value === "" ? null : Number(e.target.value) })}
                disabled={disabled}
                className={inputClass}
              />
            </View>
          )}

          {/* Noise Reduction */}
          {caps?.supportsNoiseReduction && (
            <View className="flex flex-col gap-0.5">
              <Text className={labelClass}>Noise Reduction</Text>
              <select
                value={config.noiseReductionType ?? "near_field"}
                onChange={(e) => onChange({ noiseReductionType: e.target.value })}
                disabled={disabled}
                className={selectClass}
              >
                <option value="near_field">Near Field</option>
                <option value="far_field">Far Field</option>
              </select>
            </View>
          )}

          {/* VAD Mode + Eagerness */}
          {caps?.supportsVADMode && !turnTaking.usingExternalTurnTaking && (
            <View className="flex flex-row gap-2">
              <View className="flex flex-col gap-0.5 flex-1">
                <Text className={labelClass}>VAD Mode</Text>
                <select
                  value={config.vadMode ?? caps.defaultVADMode ?? "server_vad"}
                  onChange={(e) => onChange({ vadMode: e.target.value as "server_vad" | "semantic_vad" })}
                  disabled={disabled}
                  className={selectClass}
                >
                  <option value="server_vad">Server VAD</option>
                  <option value="semantic_vad">Semantic VAD</option>
                </select>
              </View>
              {caps?.supportsVADEagerness && (config.vadMode ?? caps?.defaultVADMode) === "semantic_vad" && (
                <View className="flex flex-col gap-0.5 flex-1">
                  <Text className={labelClass}>Eagerness</Text>
                  <select
                    value={config.vadEagerness ?? caps?.defaultVADEagerness ?? "low"}
                    onChange={(e) => onChange({ vadEagerness: e.target.value as "low" | "medium" | "high" })}
                    disabled={disabled}
                    className={selectClass}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </View>
              )}
            </View>
          )}

          {/* EOT Controls */}
          {caps?.supportsEOT && (
            <View className="flex flex-row gap-2">
              <View className="flex flex-col gap-0.5 flex-1">
                <Text className={labelClass}>EOT Threshold</Text>
                <input
                  type="number"
                  min={0.5}
                  max={0.9}
                  step={0.1}
                  value={config.eotThreshold ?? caps.defaultEOTThreshold ?? 0.8}
                  onChange={(e) => onChange({ eotThreshold: e.target.value === "" ? null : Number(e.target.value) })}
                  disabled={disabled}
                  className={inputClass}
                />
              </View>
              <View className="flex flex-col gap-0.5 flex-1">
                <Text className={labelClass}>EOT Timeout (ms)</Text>
                <input
                  type="number"
                  min={100}
                  max={10000}
                  step={100}
                  value={config.eotTimeoutMs ?? caps.defaultEOTTimeoutMs ?? 2500}
                  onChange={(e) => onChange({ eotTimeoutMs: e.target.value === "" ? null : Number(e.target.value) })}
                  disabled={disabled}
                  className={inputClass}
                />
              </View>
            </View>
          )}

          {/* Smart Turn Config */}
          {turnTaking.selectedTurnTakingMode === "smart_turn" && (
            <View className="flex flex-row gap-2">
              <View className="flex flex-col gap-0.5 flex-1">
                <Text className={labelClass}>VAD Stop (s)</Text>
                <input
                  type="number"
                  min={0.1}
                  max={2.0}
                  step={0.1}
                  value={config.smartTurnVadStopSecs ?? caps?.defaultSmartTurnVadStopSecs ?? 0.4}
                  onChange={(e) => onChange({ smartTurnVadStopSecs: e.target.value === "" ? null : Number(e.target.value) })}
                  disabled={disabled}
                  className={inputClass}
                />
              </View>
              <View className="flex flex-col gap-0.5 flex-1">
                <Text className={labelClass}>ML Fallback (s)</Text>
                <input
                  type="number"
                  min={0.5}
                  max={10.0}
                  step={0.5}
                  value={config.smartTurnStopSecs ?? caps?.defaultSmartTurnStopSecs ?? 3.0}
                  onChange={(e) => onChange({ smartTurnStopSecs: e.target.value === "" ? null : Number(e.target.value) })}
                  disabled={disabled}
                  className={inputClass}
                />
              </View>
              <View className="flex flex-col gap-0.5 flex-1">
                <Text className={labelClass}>Timeout (s)</Text>
                <input
                  type="number"
                  min={1.0}
                  max={30.0}
                  step={0.5}
                  value={config.smartTurnTimeout ?? caps?.defaultSmartTurnTimeout ?? 5.0}
                  onChange={(e) => onChange({ smartTurnTimeout: e.target.value === "" ? null : Number(e.target.value) })}
                  disabled={disabled}
                  className={inputClass}
                />
              </View>
            </View>
          )}

          {/* Remove */}
          {canRemove && (
            <button
              onClick={onRemove}
              disabled={disabled}
              className="flex flex-row items-center justify-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors border disabled:opacity-40 disabled:cursor-not-allowed mt-1 text-rose-300 border-slate-700 hover:bg-rose-950/30"
            >
              <Icon
                name="close"
                size={12}
              />
              Remove Model
            </button>
          )}
        </View>
      )}
    </View>
  );
}

// --- Stats types ---

type PanelStats = {
  utteranceCount: number;
  relativeLatencyMs: number | null;
  utteranceRank: "best" | "worst" | "mid" | null;
  latencyRank: "best" | "worst" | "mid" | null;
};

type PanelStatsById = Record<string, PanelStats>;

function computeAllPanelStats(panels: ModelPanel[]): PanelStatsById {
  const withMeta = panels.map((p) => {
    const finals = p.events.filter((e) => e.type === "transcription.completed");
    return {
      panelId: p.id,
      utteranceCount: finals.length,
      lastFinalTimestamp: finals.length > 0 ? finals[finals.length - 1].timestamp.getTime() : null,
      relativeLatencyMs: null as number | null,
      utteranceRank: null as PanelStats["utteranceRank"],
      latencyRank: null as PanelStats["latencyRank"],
    };
  });

  const validTimestamps = withMeta.map((m) => m.lastFinalTimestamp).filter((t): t is number => t !== null);
  const earliest = validTimestamps.length > 0 ? Math.min(...validTimestamps) : null;

  for (const panel of withMeta) {
    panel.relativeLatencyMs = panel.lastFinalTimestamp !== null && earliest !== null ? Math.round(panel.lastFinalTimestamp - earliest) : null;
  }

  const withUtterances = withMeta.filter((s) => s.utteranceCount > 0);
  if (withUtterances.length >= 2) {
    const counts = withUtterances.map((s) => s.utteranceCount);
    const minCount = Math.min(...counts);
    const maxCount = Math.max(...counts);
    if (minCount !== maxCount) {
      for (const s of withMeta) {
        if (s.utteranceCount === 0) continue;
        if (s.utteranceCount === minCount) s.utteranceRank = "best";
        else if (s.utteranceCount === maxCount) s.utteranceRank = "worst";
        else s.utteranceRank = "mid";
      }
    }
  }

  const withLatency = withMeta.filter((s) => s.relativeLatencyMs !== null);
  if (withLatency.length >= 2) {
    const latencies = withLatency.map((s) => s.relativeLatencyMs!);
    const minLat = Math.min(...latencies);
    const maxLat = Math.max(...latencies);
    if (minLat !== maxLat) {
      for (const s of withMeta) {
        if (s.relativeLatencyMs === null) continue;
        if (s.relativeLatencyMs === minLat) s.latencyRank = "best";
        else if (s.relativeLatencyMs === maxLat) s.latencyRank = "worst";
        else s.latencyRank = "mid";
      }
    }
  }

  const byId: PanelStatsById = {};
  for (const s of withMeta) {
    byId[s.panelId] = {
      utteranceCount: s.utteranceCount,
      relativeLatencyMs: s.relativeLatencyMs,
      utteranceRank: s.utteranceRank,
      latencyRank: s.latencyRank,
    };
  }
  return byId;
}

// --- TranscribePanel sub-component ---

function TranscribePanel({
  config,
  onChange,
  allModels,
  getModelByValue,
  getProviderForModel,
  showPartials,
  showAnalyticsEvents,
  showSessionEvents,
  events,
  connectTime,
  disabled,
  canRemove,
  onRemove,
  panelCount,
  isLive,
}: {
  config: PanelConfig;
  onChange: (patch: Partial<PanelConfig>) => void;
  allModels: STTModel[];
  getModelByValue: (v: string) => STTModel | undefined;
  getProviderForModel: (v: string) => { id: string } | undefined;
  showPartials: boolean;
  showAnalyticsEvents: boolean;
  showSessionEvents: boolean;
  events: TranscribeEvent[];
  connectTime: number;
  disabled: boolean;
  canRemove: boolean;
  onRemove: () => void;
  panelCount: number;
  isLive: boolean;
}) {
  const logRef = useRef<HTMLDivElement | null>(null);
  const filtered = events.filter((e) => {
    if (!showPartials && e.type === "transcription.partial") return false;
    if (!showAnalyticsEvents && (e.type.startsWith("analytics.") || e.type.startsWith("analytics."))) return false;
    if (
      !showSessionEvents &&
      (e.type.startsWith("session.") ||
        e.type.startsWith("websocket.") ||
        (e.type.startsWith("system.") && !e.type.startsWith("analytics.")) ||
        e.type === "speech.started" ||
        e.type === "speech.stopped")
    )
      return false;
    return true;
  });
  const rows = Math.ceil(panelCount / 2);
  const heightClass = rows > 1 ? "min-h-[250px] max-h-[35vh]" : "min-h-[400px] max-h-[60vh]";
  const containerClass = "flex flex-col flex-1 min-w-0 border border-slate-700 rounded-lg bg-[#0b1120]";
  const logClass = `flex-1 ${heightClass} overflow-y-auto bg-[#0a0b18] p-2 text-sm font-mono`;
  const footerClass = "flex flex-row items-center gap-3 px-3 py-1.5 border-t border-slate-700 bg-[#111827] rounded-b-lg";

  useEffect(() => {
    if (!isLive) return;
    const node = logRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [filtered.length, isLive]);

  return (
    <View className={containerClass}>
      {/* Integrated config header */}
      <PanelHeader
        config={config}
        onChange={onChange}
        allModels={allModels}
        getModelByValue={getModelByValue}
        getProviderForModel={getProviderForModel}
        disabled={disabled}
        canRemove={canRemove}
        onRemove={onRemove}
      />
      {/* Event log */}
      <View
        ref={logRef}
        className={logClass}
      >
        {filtered.length === 0 && <Text className="text-cyan-300/60 italic text-xs px-1">Waiting for events...</Text>}
        {filtered.map((evt) => (
          <EventRow
            key={evt.id}
            event={evt}
            connectTime={connectTime}
          />
        ))}
      </View>
      <View className={footerClass} />
    </View>
  );
}

// --- Component ---

export const TranscribePage = () => {
  const { getAllModels, getModelByValue, getProviderForModel, loading } = useSTTProviders();
  const allModels = getAllModels();

  // Connection state
  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [panels, setPanels] = useState<ModelPanel[]>([
    {
      id: "panel-1",
      config: makeDefaultConfig("flux-general-en"),
      events: [],
    },
    {
      id: "panel-2",
      config: makeDefaultConfig("gpt-4o-transcribe"),
      events: [],
    },
  ]);
  const [micOn, setMicOn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPartials, setShowPartials] = useState(true);
  const [showAnalyticsEvents, setShowAnalyticsEvents] = useState(true);
  const [showSessionEvents, setShowSessionEvents] = useState(true);
  const [playingFile, setPlayingFile] = useState<string | null>(null);

  // Per-panel refs
  const panelRefs = useRef<Record<string, PanelRef>>({});
  const panelIdCounterRef = useRef(2);
  const targetReadyCountRef = useRef(0);
  const readyPanelIdsRef = useRef<Set<string>>(new Set());
  const livePanelIdsRef = useRef<Set<string>>(new Set());
  const disconnectingRef = useRef(false);

  // Connect time ref for elapsed timestamps
  const connectTimeRef = useRef<number>(Date.now());

  // Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileAudioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingFileRef = useRef<File | null>(null);

  const getPanelRef = useCallback((panelId: string): PanelRef => {
    if (!panelRefs.current[panelId]) {
      panelRefs.current[panelId] = makePanelRef();
    }
    return panelRefs.current[panelId];
  }, []);

  const addPanelEvent = useCallback(
    (panelId: string, evt: Omit<TranscribeEvent, "id" | "timestamp">) => {
      const panelRef = getPanelRef(panelId);
      const id = ++panelRef.eventId;
      setPanels((prev) =>
        prev.map((p) =>
          p.id === panelId
            ? {
                ...p,
                events: [...p.events, { ...evt, id, timestamp: new Date() }],
              }
            : p,
        ),
      );
    },
    [getPanelRef],
  );

  const updatePanelConfig = useCallback((panelId: string, patch: Partial<PanelConfig>) => {
    setPanels((prev) =>
      prev.map((p) =>
        p.id === panelId
          ? {
              ...p,
              config: { ...p.config, ...patch },
            }
          : p,
      ),
    );
  }, []);

  const appendSummaryEvents = useCallback(
    (currentPanels: ModelPanel[]): ModelPanel[] => {
      const statsById = computeAllPanelStats(currentPanels);

      return currentPanels.map((panel) => {
        const stats = statsById[panel.id];
        if (!stats) return panel;

        const panelRef = getPanelRef(panel.id);
        const nextEvents = [...panel.events];
        const pushSystem = (type: string, text: string) => {
          const id = ++panelRef.eventId;
          nextEvents.push({
            id,
            timestamp: new Date(),
            type,
            text,
            raw: { type, text },
          });
        };

        const utteranceSummary =
          stats.utteranceRank === "best"
            ? `${stats.utteranceCount} (best)`
            : stats.utteranceRank === "worst"
              ? `${stats.utteranceCount} (worst)`
              : `${stats.utteranceCount}`;
        pushSystem("analytics.utterances", `turns: ${utteranceSummary}`);

        if (stats.relativeLatencyMs !== null) {
          const latencyValue = stats.relativeLatencyMs === 0 ? "±0ms" : `+${stats.relativeLatencyMs}ms`;
          const latencySummary =
            stats.latencyRank === "best" ? `${latencyValue} (best)` : stats.latencyRank === "worst" ? `${latencyValue} (worst)` : latencyValue;
          pushSystem("analytics.relative_latency", `Relative Turn-taking latency: ${latencySummary}`);
        }

        return { ...panel, events: nextEvents };
      });
    },
    [getPanelRef],
  );

  // --- Mic control (defined early so setupWs can reference it) ---

  const stopMic = useCallback(() => {
    if (fileTimerRef.current) {
      clearTimeout(fileTimerRef.current);
      fileTimerRef.current = null;
    }
    if (fileAudioRef.current) {
      fileAudioRef.current.pause();
      URL.revokeObjectURL(fileAudioRef.current.src);
      fileAudioRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setMicOn(false);
    setPlayingFile(null);
    setStatus((prev) => (prev === "listening" ? "connected" : prev));
  }, []);

  const addModelPanel = useCallback(() => {
    if (status !== "disconnected") return;
    if (panels.length >= 4) return;
    panelIdCounterRef.current += 1;
    const nextModel = allModels[panels.length % Math.max(allModels.length, 1)]?.value ?? "gpt-4o-transcribe";
    setPanels((prev) => [
      ...prev,
      {
        id: `panel-${panelIdCounterRef.current}`,
        config: makeDefaultConfig(nextModel),
        events: [],
      },
    ]);
  }, [allModels, panels.length, status]);

  const removeModelPanel = useCallback(
    (panelId: string) => {
      if (status !== "disconnected") return;
      setPanels((prev) => {
        if (prev.length <= 1) return prev;
        return prev.filter((p) => p.id !== panelId);
      });
      delete panelRefs.current[panelId];
    },
    [status],
  );

  // --- WebSocket setup for one panel ---

  const setupWs = useCallback(
    (ws: WebSocket, panelId: string, panelConfig: PanelConfig, providerId: string) => {
      ws.onopen = () => {
        addPanelEvent(panelId, { type: "websocket.connected", raw: { type: "websocket.connected" } });
      };

      ws.onmessage = (e) => {
        let msg: Record<string, unknown>;
        try {
          msg = JSON.parse(e.data as string);
        } catch {
          return;
        }

        const type = msg.type as string;

        if (type === "session.created") {
          const session: Record<string, unknown> = {
            model: panelConfig.modelValue,
            provider: providerId.toUpperCase(),
            language: panelConfig.language,
            input_audio_format: "pcm16",
            sample_rate: SAMPLE_RATE,
          };
          const modelCaps = getModelByValue(panelConfig.modelValue)?.capabilities;
          const turnTaking = resolveTurnTakingConfig(panelConfig, modelCaps);
          if (panelConfig.prompt) session.prompt = panelConfig.prompt;
          if (turnTaking.supportsTurnTakingMode || !turnTaking.hasBuiltinVAD) {
            session.turn_taking_mode = turnTaking.selectedTurnTakingMode;
          }
          if (!turnTaking.usingExternalTurnTaking && panelConfig.endpointing !== null) {
            session.endpointing = panelConfig.endpointing;
          }
          if (turnTaking.usingExternalTurnTaking) {
            const externalEndpointing =
              panelConfig.externalEndpointing ?? panelConfig.endpointing ?? modelCaps?.defaultExternalEndpointing ?? modelCaps?.defaultEndpointing;
            if (externalEndpointing !== null && externalEndpointing !== undefined) {
              session.external_endpointing = externalEndpointing;
            }
          }
          if (panelConfig.noiseReductionType) session.noise_reduction_type = panelConfig.noiseReductionType;
          if (!turnTaking.usingExternalTurnTaking && panelConfig.vadMode) session.vad_mode = panelConfig.vadMode;
          if (!turnTaking.usingExternalTurnTaking && panelConfig.vadEagerness) session.vad_eagerness = panelConfig.vadEagerness;
          if (panelConfig.eotThreshold !== null) session.eot_threshold = panelConfig.eotThreshold;
          if (panelConfig.eotTimeoutMs !== null) session.eot_timeout_ms = panelConfig.eotTimeoutMs;
          if (turnTaking.selectedTurnTakingMode === "smart_turn") {
            if (panelConfig.smartTurnVadStopSecs !== null) session.smart_turn_vad_stop_secs = panelConfig.smartTurnVadStopSecs;
            if (panelConfig.smartTurnStopSecs !== null) session.smart_turn_stop_secs = panelConfig.smartTurnStopSecs;
            if (panelConfig.smartTurnTimeout !== null) session.smart_turn_timeout = panelConfig.smartTurnTimeout;
          }
          ws.send(JSON.stringify({ type: "session.update", session }));
          addPanelEvent(panelId, { type, raw: msg });
          return;
        }

        if (type === "session.updated") {
          readyPanelIdsRef.current.add(panelId);
          if (targetReadyCountRef.current > 0 && readyPanelIdsRef.current.size >= targetReadyCountRef.current) {
            setStatus("connected");
          }
          addPanelEvent(panelId, { type, raw: msg });
          return;
        }

        const text = (msg.text as string) ?? ((msg.transcription as Record<string, unknown>)?.text as string) ?? undefined;

        addPanelEvent(panelId, { type, text, raw: msg });
      };

      ws.onerror = () => {
        setError(`WebSocket error for ${panelConfig.modelValue}`);
      };

      ws.onclose = (ev) => {
        addPanelEvent(panelId, {
          type: "websocket.disconnected",
          raw: { type: "websocket.disconnected", code: ev.code },
        });

        const panel = getPanelRef(panelId);
        panel.ws = null;
        livePanelIdsRef.current.delete(panelId);

        if (disconnectingRef.current) return;

        readyPanelIdsRef.current.delete(panelId);
        if (targetReadyCountRef.current > 0) {
          targetReadyCountRef.current -= 1;
        }

        if (ev.code === 4001) {
          setError("Unauthorized — session may have expired");
        } else if (ev.code !== 1000) {
          setError(`Connection closed for ${panelConfig.modelValue} (${ev.code})`);
        }

        if (livePanelIdsRef.current.size === 0) {
          setPanels((currentPanels) => appendSummaryEvents(currentPanels));
          stopMic();
          setStatus("disconnected");
          return;
        }

        if (targetReadyCountRef.current > 0 && readyPanelIdsRef.current.size >= targetReadyCountRef.current) {
          setStatus((prev) => (prev === "connecting" ? "connected" : prev));
        }
      };
    },
    [addPanelEvent, getModelByValue, getPanelRef, stopMic],
  );

  // --- Connect / disconnect ---

  const connect = useCallback(async () => {
    if (!TRANSCRIBE_WS_URL) {
      setError("TRANSCRIBE_WS_URL not configured for this environment");
      return;
    }

    const token = await fetchWsToken();
    if (!token) {
      setError("Failed to get auth token — are you logged in?");
      return;
    }

    if (panels.length === 0) {
      setError("Add at least one model to connect");
      return;
    }

    setError(null);
    setPanels((prev) => prev.map((p) => ({ ...p, events: [] })));
    disconnectingRef.current = false;
    readyPanelIdsRef.current = new Set();
    connectTimeRef.current = Date.now();
    setStatus("connecting");

    const url = `${TRANSCRIBE_WS_URL}?token=${encodeURIComponent(token)}`;
    const panelSnapshot = panels;
    targetReadyCountRef.current = panelSnapshot.length;
    livePanelIdsRef.current = new Set(panelSnapshot.map((panel) => panel.id));
    panelRefs.current = {};

    for (const panel of panelSnapshot) {
      const ws = new WebSocket(url);
      const panelRef = makePanelRef();
      panelRef.ws = ws;
      panelRefs.current[panel.id] = panelRef;
      const provider = getProviderForModel(panel.config.modelValue);
      setupWs(ws, panel.id, panel.config, provider?.id ?? "");
    }
  }, [panels, setupWs, getProviderForModel]);

  const disconnect = useCallback(() => {
    disconnectingRef.current = true;

    for (const [panelId, panel] of Object.entries(panelRefs.current)) {
      const ws = panel.ws;
      if (ws) {
        addPanelEvent(panelId, {
          type: "websocket.disconnected",
          raw: { type: "websocket.disconnected", code: 1000, reason: "user_disconnect" },
        });
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "session.close" }));
        }
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
      }
      panel.ws = null;
    }

    livePanelIdsRef.current = new Set();
    readyPanelIdsRef.current = new Set();
    targetReadyCountRef.current = 0;
    disconnectingRef.current = false;

    pendingFileRef.current = null;
    setPanels((currentPanels) => appendSummaryEvents(currentPanels));
    setStatus("disconnected");
    stopMic();
  }, [addPanelEvent, appendSummaryEvents, stopMic]);

  // --- Mic control ---

  const startMic = useCallback(async () => {
    const openSockets = Object.values(panelRefs.current).filter((p) => p.ws && p.ws.readyState === WebSocket.OPEN);
    if (openSockets.length === 0) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);

      const sendAudioToBoth = (float32: Float32Array) => {
        const payload = JSON.stringify({
          type: "audio.append",
          audio: float32ToInt16Base64(float32),
        });
        for (const panel of Object.values(panelRefs.current)) {
          const ws = panel.ws;
          if (ws && ws.readyState === WebSocket.OPEN) ws.send(payload);
        }
      };

      try {
        const workletCode = `
          class PcmProcessor extends AudioWorkletProcessor {
            constructor() {
              super();
              this.buffer = new Float32Array(0);
            }
            process(inputs) {
              const input = inputs[0][0];
              if (!input) return true;
              const newBuf = new Float32Array(this.buffer.length + input.length);
              newBuf.set(this.buffer);
              newBuf.set(input, this.buffer.length);
              this.buffer = newBuf;
              while (this.buffer.length >= ${CHUNK_SAMPLES}) {
                const chunk = this.buffer.slice(0, ${CHUNK_SAMPLES});
                this.buffer = this.buffer.slice(${CHUNK_SAMPLES});
                this.port.postMessage(chunk);
              }
              return true;
            }
          }
          registerProcessor('pcm-processor', PcmProcessor);
        `;
        const blob = new Blob([workletCode], { type: "application/javascript" });
        const blobUrl = URL.createObjectURL(blob);
        await ctx.audioWorklet.addModule(blobUrl);
        URL.revokeObjectURL(blobUrl);

        const workletNode = new AudioWorkletNode(ctx, "pcm-processor");
        workletNode.port.onmessage = (ev: MessageEvent<Float32Array>) => {
          sendAudioToBoth(ev.data);
        };
        source.connect(workletNode);
        workletNode.connect(ctx.destination);
        workletNodeRef.current = workletNode;
      } catch {
        const processor = ctx.createScriptProcessor(CHUNK_SAMPLES, 1, 1);
        processor.onaudioprocess = (ev) => {
          sendAudioToBoth(ev.inputBuffer.getChannelData(0));
        };
        source.connect(processor);
        processor.connect(ctx.destination);
        workletNodeRef.current = processor;
      }

      setMicOn(true);
      setStatus("listening");
    } catch (err) {
      setError(`Mic access denied: ${err}`);
    }
  }, []);

  // Auto-start mic or pending file playback when panels are connected
  useEffect(() => {
    if (status !== "connected" || micOn || playingFile) return;

    if (pendingFileRef.current) {
      const file = pendingFileRef.current;
      pendingFileRef.current = null;
      startFilePlayback(file);
    } else {
      startMic();
    }
  }, [status]);

  const startFilePlayback = useCallback(
    async (file: File) => {
      const openSockets = Object.values(panelRefs.current).filter((p) => p.ws && p.ws.readyState === WebSocket.OPEN);
      if (openSockets.length === 0) return;

      stopMic();

      try {
        const arrayBuffer = await file.arrayBuffer();

        // Decode at native sample rate
        const tempCtx = new AudioContext();
        const decodedBuffer = await tempCtx.decodeAudioData(arrayBuffer);
        await tempCtx.close();

        // Resample to SAMPLE_RATE mono via OfflineAudioContext
        const numSamples = Math.ceil(decodedBuffer.duration * SAMPLE_RATE);
        const offlineCtx = new OfflineAudioContext(1, numSamples, SAMPLE_RATE);
        const offlineSrc = offlineCtx.createBufferSource();
        offlineSrc.buffer = decodedBuffer;
        offlineSrc.connect(offlineCtx.destination);
        offlineSrc.start();
        const resampledBuffer = await offlineCtx.startRendering();
        const pcmData = resampledBuffer.getChannelData(0);

        // Set up speaker playback and sync PCM streaming to it
        const audio = new Audio(URL.createObjectURL(file));
        fileAudioRef.current = audio;

        const chunkIntervalMs = (CHUNK_SAMPLES / SAMPLE_RATE) * 1000;
        let sentSamples = 0;

        const tick = () => {
          const targetSamples = Math.floor(audio.currentTime * SAMPLE_RATE);

          while (sentSamples < targetSamples && sentSamples < pcmData.length) {
            const end = Math.min(sentSamples + CHUNK_SAMPLES, pcmData.length);
            const chunk = pcmData.slice(sentSamples, end);
            sentSamples = end;

            const payload = JSON.stringify({
              type: "audio.append",
              audio: float32ToInt16Base64(chunk),
            });
            for (const panel of Object.values(panelRefs.current)) {
              const ws = panel.ws;
              if (ws && ws.readyState === WebSocket.OPEN) ws.send(payload);
            }
          }

          if (sentSamples >= pcmData.length) {
            fileTimerRef.current = setTimeout(() => {
              fileTimerRef.current = null;
              disconnect();
            }, 5000);
            return;
          }

          fileTimerRef.current = setTimeout(tick, chunkIntervalMs);
        };

        setPlayingFile(file.name);
        setStatus("listening");
        await audio.play();
        fileTimerRef.current = setTimeout(tick, chunkIntervalMs);
      } catch (err) {
        setError(`Failed to play audio file: ${err}`);
      }
    },
    [stopMic, disconnect],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";

      if (status === "disconnected") {
        pendingFileRef.current = file;
        connect();
      } else {
        startFilePlayback(file);
      }
    },
    [startFilePlayback, connect, status],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      for (const panel of Object.values(panelRefs.current)) {
        panel.ws?.close();
      }
      if (fileTimerRef.current) {
        clearTimeout(fileTimerRef.current);
        fileTimerRef.current = null;
      }
      if (fileAudioRef.current) {
        fileAudioRef.current.pause();
        URL.revokeObjectURL(fileAudioRef.current.src);
        fileAudioRef.current = null;
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      audioContextRef.current?.close();
    };
  }, []);

  // --- Render ---

  const isConnected = status === "connected" || status === "listening";

  const statusColor = {
    disconnected: "bg-gray-400",
    connecting: "bg-yellow-400 animate-pulse",
    connected: "bg-emerald-400",
    listening: "bg-emerald-400 animate-pulse",
  }[status];

  const statusLabel = {
    disconnected: "Offline",
    connecting: "Connecting",
    connected: "Connected",
    listening: "Listening",
  }[status];

  if (loading) {
    return (
      <PageLayout
        title="STT Lab"
        description="Live transcription from microphone or audio file — side-by-side model comparison"
      >
        <View className="flex items-center justify-center p-12">
          <Text className="text-sm text-gray-500">Loading STT providers...</Text>
        </View>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="STT Lab"
      description="Live transcription from microphone or audio file — side-by-side model comparison"
      headerAction={
        <View className="flex flex-row items-center gap-3">
          {/* Status indicator */}
          <View className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <View className={`w-2 h-2 rounded-full ${statusColor}`} />
            <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">{statusLabel}</Text>
          </View>

          {/* Speak / Stop */}
          <button
            onClick={isConnected ? disconnect : connect}
            disabled={status === "connecting"}
          >
            <Icon
              name={isConnected ? "stop" : "mic"}
              size={14}
            />
            {isConnected ? "Stop" : status === "connecting" ? "Connecting..." : "Speak"}
          </button>

          {/* Upload Audio / playback indicator */}
          {playingFile ? (
            <View className="flex flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800">
              <View className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
              <Text className="text-sm max-w-[140px] truncate">{playingFile}</Text>
              <button
                onClick={disconnect}
                className="ml-0.5 p-0.5 rounded hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
              >
                <Icon
                  name="close"
                  size={12}
                />
              </button>
            </View>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={status === "connecting"}
              className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border disabled:opacity-40 disabled:cursor-not-allowed bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/50"
            >
              <Icon
                name="upload"
                size={14}
              />
              Upload Audio
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </View>
      }
    >
      <View className="flex flex-col gap-3 mt-4">
        {error && (
          <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <Icon
              name="error"
              size={14}
              className="text-red-500"
            />
            <Text className="text-sm text-red-700 dark:text-red-300">{error}</Text>
          </View>
        )}

        {/* Toolbar: filters + add model */}
        <View className="flex flex-row items-center justify-between">
          <View className="flex flex-row items-center gap-4">
            <label className="flex flex-row items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showPartials}
                onChange={(e) => setShowPartials(e.target.checked)}
                className="accent-blue-500 w-3.5 h-3.5 rounded"
              />
              <Text className="text-xs text-gray-500 dark:text-gray-400">Partials</Text>
            </label>
            <label className="flex flex-row items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showAnalyticsEvents}
                onChange={(e) => setShowAnalyticsEvents(e.target.checked)}
                className="accent-blue-500 w-3.5 h-3.5 rounded"
              />
              <Text className="text-xs text-gray-500 dark:text-gray-400">Analytics</Text>
            </label>
            <label className="flex flex-row items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showSessionEvents}
                onChange={(e) => setShowSessionEvents(e.target.checked)}
                className="accent-blue-500 w-3.5 h-3.5 rounded"
              />
              <Text className="text-xs text-gray-500 dark:text-gray-400">System events</Text>
            </label>
          </View>
          <button
            onClick={addModelPanel}
            disabled={status !== "disconnected" || panels.length >= 4}
            className="flex flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
          >
            <Icon
              name="add"
              size={14}
            />
            Add Model
          </button>
        </View>

        {/* Panels */}
        <View
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(panels.length, 1), 2)}, minmax(0, 1fr))` }}
        >
          {panels.map((panel) => (
            <TranscribePanel
              key={panel.id}
              config={panel.config}
              onChange={(patch) => updatePanelConfig(panel.id, patch)}
              allModels={allModels}
              getModelByValue={getModelByValue}
              getProviderForModel={getProviderForModel}
              events={panel.events}
              showPartials={showPartials}
              showAnalyticsEvents={showAnalyticsEvents}
              showSessionEvents={showSessionEvents}
              connectTime={connectTimeRef.current}
              disabled={isConnected}
              canRemove={panels.length > 1}
              onRemove={() => removeModelPanel(panel.id)}
              panelCount={panels.length}
              isLive={isConnected}
            />
          ))}
        </View>
      </View>
    </PageLayout>
  );
};

// --- Event row ---

function formatElapsed(eventTime: Date, connectTime: number): string {
  const diffMs = Math.max(0, eventTime.getTime() - connectTime);
  const totalSeconds = Math.floor(diffMs / 1000);
  const ms = diffMs % 1000;
  const s = String(totalSeconds).padStart(2, "0");
  const m = String(ms).padStart(3, "0");
  return `${s}.${m}`;
}

function EventRow({ event, connectTime }: { event: TranscribeEvent; connectTime: number }) {
  const time = formatElapsed(event.timestamp, connectTime);

  const isPartial = event.type === "transcription.partial";
  const isFinal = event.type === "transcription.completed";
  const isSpeech = event.type === "speech.started" || event.type === "speech.stopped";
  const isSession = event.type.startsWith("session.");
  const isWebsocket = event.type.startsWith("websocket.");
  const isError = event.type === "error";
  const hasText = !!event.text;

  const terminalTypeClass = isError
    ? "text-rose-300"
    : isFinal
      ? "text-emerald-300"
      : isPartial
        ? "text-amber-300"
        : isSpeech
          ? "text-cyan-300"
          : isSession || isWebsocket
            ? "text-violet-300"
            : "text-fuchsia-300";
  const terminalValueClass = isError ? "text-rose-200" : isPartial ? "text-amber-200/80 italic" : "text-slate-100";
  const terminalRowClass = "grid grid-cols-[48px_156px_minmax(0,1fr)] items-start px-1.5 py-0.5";

  if (hasText) {
    return (
      <View className={terminalRowClass}>
        <Text className="text-[10px] text-cyan-400/80 tabular-nums">{time}</Text>
        <Text className={`text-[10px] tabular-nums truncate ${terminalTypeClass}`}>{event.type}</Text>
        <Text className={`text-[12px] leading-relaxed min-w-0 ${terminalValueClass}`}>{event.text}</Text>
      </View>
    );
  }

  if (isError) {
    return (
      <View className={`${terminalRowClass} bg-rose-950/20`}>
        <Text className="text-[10px] text-rose-400 tabular-nums">{time}</Text>
        <Text className="text-[10px] tabular-nums text-rose-300">error</Text>
        <Text className="text-[11px] text-rose-200 truncate">{(event.raw.error as Record<string, unknown>)?.message as string}</Text>
      </View>
    );
  }

  return (
    <View className={terminalRowClass}>
      <Text className="text-[10px] text-cyan-400/80 tabular-nums">{time}</Text>
      <Text className={`text-[10px] truncate ${terminalTypeClass}`}>{event.type}</Text>
      <Text className="text-[11px] text-slate-500/80 truncate">{event.text ?? ""}</Text>
    </View>
  );
}
